'use client'

import { useState, useTransition } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { cn } from '@shared/utils'
import { TIMING_CONSTANTS } from '@shared/constants'

/**
 * Props for the `LoadingLink` component.
 */
interface LoadingLinkProps extends React.ComponentProps<typeof Link> {
  /**
   * Optional Tailwind classes to apply while the link is in a loading state.
   */
  loadingClassName?: string

  /**
   * Optional callback triggered just before navigation begins.
   */
  onNavigate?: () => void
}

/**
 * `LoadingLink` is a wrapper around `next/link` that displays a loading state
 * when clicked and navigates using `startTransition` for smooth transitions.
 *
 * It’s useful for links that trigger page transitions, especially in Next.js apps
 * using the app router and React 18 concurrent features.
 *
 * @example
 * ```tsx
 * <LoadingLink
 *   href="/dashboard"
 *   loadingClassName="opacity-50"
 *   onNavigate={() => console.log("navigating...")}
 * >
 *   Go to Dashboard
 * </LoadingLink>
 * ```
 */
export function LoadingLink({
  children,
  loadingClassName,
  className,
  onClick,
  onNavigate,
  ...props
}: LoadingLinkProps) {
  const router = useRouter()
  const [isPending, startTransition] = useTransition()
  const [isNavigating, setIsNavigating] = useState(false)

  const isLoading = isPending || isNavigating

  /**
   * Handles the link click by setting the loading state and
   * starting a concurrent navigation transition.
   */
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (onClick) {
      onClick(e)
    }

    if (!e.defaultPrevented) {
      setIsNavigating(true)

      // Trigger optional navigation hook and start transition
      startTransition(() => {
        if (onNavigate) {
          onNavigate()
        }
        router.push(props.href.toString())
      })

      // Reset loading state in case navigation hangs
      setTimeout(() => {
        setIsNavigating(false)
      }, TIMING_CONSTANTS.LOADING_RESET_DELAY)
    }
  }

  return (
    <Link
      {...props}
      onClick={handleClick}
      className={cn(className, isLoading && loadingClassName)}
      aria-disabled={isLoading}
    >
      {children}
    </Link>
  )
}
